import { useEffect, useRef, useCallback, useMemo } from 'react';
import type {
  SSEEvent,
  ErrorDisplay,
  LoopIterationEvent,
  WorkflowStatusEvent,
  WorkflowArtifactEvent,
  WorkflowDispatchEvent,
  WorkflowOutputPreviewEvent,
  DagNodeEvent,
  ApprovalGateEvent,
  SuggestedRepliesEvent,
} from '@/lib/types';
import { SSE_BASE_URL } from '@/lib/api';
import { useEventStream } from '@/hooks/useEventStream';

function parseSSEEvent(raw: string): SSEEvent | null {
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (!parsed || typeof parsed.type !== 'string') {
      console.error('[SSE] Malformed event: missing type field', { raw });
      return null;
    }
    return parsed as unknown as SSEEvent;
  } catch (parseErr) {
    console.error('[SSE] Failed to parse event:', {
      raw,
      error: (parseErr as Error).message,
    });
    return null;
  }
}

interface SSEHandlers {
  onText: (content: string, workflowResult?: { workflowName: string; runId: string }) => void;
  onToolCall: (name: string, input: Record<string, unknown>, toolCallId?: string) => void;
  onToolResult: (name: string, output: string, duration: number, toolCallId?: string) => void;
  onError: (error: ErrorDisplay) => void;
  onLockChange: (locked: boolean, queuePosition?: number) => void;
  onSessionInfo: (sessionId: string, cost?: number) => void;
  onWorkflowStatus?: (event: WorkflowStatusEvent) => void;
  onWorkflowArtifact?: (event: WorkflowArtifactEvent) => void;
  onDagNode?: (event: DagNodeEvent) => void;
  onLoopIteration?: (event: LoopIterationEvent) => void;
  onWorkflowDispatch?: (event: WorkflowDispatchEvent) => void;
  onWorkflowOutputPreview?: (event: WorkflowOutputPreviewEvent) => void;
  onApprovalGate?: (event: ApprovalGateEvent) => void;
  onSuggestedReplies?: (event: SuggestedRepliesEvent) => void;
  onWarning?: (message: string) => void;
  onRetract?: () => void;
  onSystemStatus?: (content: string) => void;
}

export function useSSE(
  conversationId: string | null,
  handlers: SSEHandlers
): { connected: boolean } {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  // Text batching: accumulate text for 50ms before dispatching
  const textBufferRef = useRef('');
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingWorkflowResultRef = useRef<{ workflowName: string; runId: string } | undefined>(
    undefined
  );

  const flushText = useCallback((): void => {
    if (textBufferRef.current) {
      handlersRef.current.onText(textBufferRef.current, pendingWorkflowResultRef.current);
      textBufferRef.current = '';
      pendingWorkflowResultRef.current = undefined;
    }
    flushTimerRef.current = null;
  }, []);

  const url = useMemo(
    () =>
      conversationId ? `${SSE_BASE_URL}/api/stream/${encodeURIComponent(conversationId)}` : null,
    [conversationId]
  );

  const handleMessage = useCallback(
    (event: MessageEvent): void => {
      const data = parseSSEEvent(event.data as string);
      if (!data) {
        handlersRef.current.onError({
          message: 'Received malformed response from server',
          classification: 'transient',
          suggestedActions: ['Refresh the page if chat appears stuck'],
        });
        return;
      }

      try {
        const h = handlersRef.current;

        switch (data.type) {
          case 'text':
            textBufferRef.current += data.content;
            if (
              'workflowResult' in data &&
              data.workflowResult &&
              typeof data.workflowResult === 'object'
            ) {
              pendingWorkflowResultRef.current = data.workflowResult as {
                workflowName: string;
                runId: string;
              };
            }
            if (!flushTimerRef.current) {
              flushTimerRef.current = setTimeout(flushText, 50);
            }
            break;
          case 'tool_call':
            // Flush buffered text before tool events to ensure text
            // attaches to the correct message (not the previous one)
            if (textBufferRef.current) {
              if (flushTimerRef.current) {
                clearTimeout(flushTimerRef.current);
                flushTimerRef.current = null;
              }
              flushText();
            }
            h.onToolCall(data.name, data.input, data.toolCallId);
            break;
          case 'tool_result':
            // Flush buffered text before tool result too
            if (textBufferRef.current) {
              if (flushTimerRef.current) {
                clearTimeout(flushTimerRef.current);
                flushTimerRef.current = null;
              }
              flushText();
            }
            h.onToolResult(data.name, data.output, data.duration, data.toolCallId);
            break;
          case 'error':
            h.onError({
              message: data.message,
              classification: data.classification ?? 'transient',
              suggestedActions: data.suggestedActions ?? [],
            });
            break;
          case 'conversation_lock':
            // Flush any buffered text before processing lock change,
            // otherwise text arriving just before lock release creates
            // a streaming message that never gets cleared.
            if (!data.locked && textBufferRef.current) {
              if (flushTimerRef.current) {
                clearTimeout(flushTimerRef.current);
                flushTimerRef.current = null;
              }
              flushText();
            }
            h.onLockChange(data.locked, data.queuePosition);
            break;
          case 'session_info':
            h.onSessionInfo(data.sessionId, data.cost);
            break;
          case 'workflow_status':
            h.onWorkflowStatus?.(data);
            if (
              data.status === 'completed' ||
              data.status === 'failed' ||
              data.status === 'cancelled'
            ) {
              h.onLockChange(false);
            }
            break;
          case 'workflow_artifact':
            h.onWorkflowArtifact?.(data);
            break;
          case 'approval_gate':
            h.onApprovalGate?.(data);
            break;
          case 'suggested_replies':
            // Reserved protocol: no server producer emits this yet. The handler is
            // optional so a future producer (workflow/gate) can drive the quick-reply
            // row without re-touching useSSE. Until then the client derives suggestions
            // from assistant-message markers (parseSuggestedReplies).
            h.onSuggestedReplies?.(data);
            break;
          case 'dag_node':
            h.onDagNode?.(data);
            break;
          case 'workflow_step':
            h.onLoopIteration?.(data);
            break;
          case 'workflow_dispatch':
            // Flush buffered text before dispatch events to ensure the dispatch
            // message (🚀) is committed as an assistant message before
            // onWorkflowDispatch attaches metadata to the "last assistant message".
            if (textBufferRef.current) {
              if (flushTimerRef.current) {
                clearTimeout(flushTimerRef.current);
                flushTimerRef.current = null;
              }
              flushText();
            }
            h.onWorkflowDispatch?.(data);
            break;
          case 'workflow_output_preview':
            h.onWorkflowOutputPreview?.(data);
            break;
          case 'lane_started':
          case 'lane_completed':
          case 'lane_failed':
          case 'aggregate_started':
          case 'aggregate_completed':
            // Currently additive events for external consumers; dashboard/UI
            // remains driven by dag_node + workflow_status.
            break;
          case 'warning':
            h.onWarning?.(data.message);
            break;
          case 'system_status':
            h.onSystemStatus?.(data.content);
            break;
          case 'retract':
            // Discard any buffered text (don't flush to UI)
            if (flushTimerRef.current) {
              clearTimeout(flushTimerRef.current);
              flushTimerRef.current = null;
            }
            textBufferRef.current = '';
            pendingWorkflowResultRef.current = undefined;
            h.onRetract?.();
            break;
          case 'heartbeat':
            break;
          default: {
            console.warn('[SSE] Unknown event type', { type: (data as { type: string }).type });
            break;
          }
        }
      } catch (handlerError) {
        console.error('[SSE] Handler error for event type:', data.type, handlerError);
        try {
          handlersRef.current.onError({
            message: `Failed to process ${data.type} event. UI may be out of sync.`,
            classification: 'transient',
            suggestedActions: ['Refresh the page if chat appears stuck'],
          });
        } catch {
          // Avoid infinite loop if onError itself throws
        }
      }
    },
    [flushText]
  );

  // Connection lifecycle (open/reconnect/backoff/foreground+online resume) is
  // owned by useEventStream — never open a second EventSource for the same stream.
  const { status } = useEventStream({ url, onMessage: handleMessage });

  // Flush any buffered text when the stream URL changes (conversation switch or
  // teardown). The EventSource itself is closed by useEventStream's cleanup.
  useEffect(() => {
    return (): void => {
      if (flushTimerRef.current) {
        clearTimeout(flushTimerRef.current);
        flushText();
      }
    };
  }, [url, flushText]);

  // Surface a hard, user-facing error only after the manual-backoff escalation
  // (>= 4 consecutive failed reconnects) — not on every transient drop. Transient
  // reconnecting states are conveyed by `status` and the connectivity UI, so the
  // old per-error "refresh the page" spam is gone.
  const escalatedRef = useRef(false);
  // Reset escalation when the stream URL changes (conversation switch). Without
  // this, switching away from a stream that was already in `error` leaves
  // escalatedRef stuck true, so the new conversation's failure is silently
  // suppressed (the status effect won't re-fire if status stays 'error').
  useEffect(() => {
    escalatedRef.current = false;
  }, [url]);
  useEffect(() => {
    if (status === 'error') {
      if (!escalatedRef.current) {
        escalatedRef.current = true;
        handlersRef.current.onError({
          message: 'Lost connection to server. Please refresh the page.',
          classification: 'transient',
          suggestedActions: ['Refresh the page', 'Check that the server is running'],
        });
      }
    } else if (status === 'open') {
      // Reconnected — allow a future escalation to fire again.
      escalatedRef.current = false;
    }
  }, [status]);

  return { connected: status === 'open' };
}
